/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/HomeCalendarDayModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AccessTime, PersonOutline } from "@mui/icons-material";
import styles from "./index.module.css";
import { formatDayHeading } from "../../logic/homeCalendar";

const CALENDAR_EVENT_TYPES = new Set([
  "event",
  "businessEvent",
  "b2bEvent",
  "breakfastEvent",
  "virtualEvent",
]);

/**
 * Calendar event types that already ended are shown as concluded.
 * Appointments stay unchanged.
 * @param {Object} item
 * @returns {boolean}
 */
const isConcludedEvent = (item) =>
  CALENDAR_EVENT_TYPES.has(item.type) && Boolean(item.isPast);

/**
 * Day list for the home calendar: every event and appointment of a single day.
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is shown
 * @param {Date} props.date - Day the list belongs to
 * @param {Array} props.items - Agenda items from `getDayAgendaForDate`
 * @param {number} props.appointmentCount - Appointments scheduled that day
 * @param {Function} props.onClose - Called when the dialog is dismissed
 */
const HomeCalendarDayModal = ({
  open,
  date,
  items = [],
  appointmentCount = 0,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    } else {
      // Dialog already matches the desired open state
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    const handleClose = () => onCloseRef.current();

    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, []);

  const formatRange = (item) =>
    item.endTime ? `${item.time} - ${item.endTime}` : item.time;

  return (
    <dialog ref={dialogRef} className={styles.dayModal}>
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>
          <span className={styles.modalTitleLabel}>
            {t("components.homeCalendarDayModal.title")}
          </span>{" "}
          <span className={styles.modalTitleDate}>
            {formatDayHeading(date, i18n.language)}
          </span>
        </h2>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("common.close")}
        >
          &times;
        </button>
      </div>

      <span className={styles.countBadge}>
        {t("components.homeCalendarDayModal.scheduledAppointments", {
          count: appointmentCount,
        })}
      </span>

      <div className={styles.itemList}>
        {items.length === 0 && (
          <p className={styles.noResults}>
            {t("components.homeCalendarDayModal.noResults")}
          </p>
        )}
        {items.map((item) => {
          const isEvent = CALENDAR_EVENT_TYPES.has(item.type);
          const isPastEvent = isConcludedEvent(item);

          return (
            <div
              key={`${item.type}-${item.id}`}
              className={`${styles.item} ${isEvent ? styles.itemEvent : ""} ${
                isPastEvent ? styles.itemPast : ""
              }`}
              title={
                isPastEvent
                  ? t("components.homeCalendar.eventConcluded")
                  : undefined
              }
            >
              <div className={styles.itemRow}>
                <AccessTime className={styles.itemIcon} />
                <span className={styles.itemTime}>{formatRange(item)}</span>
                {isEvent && <span>-</span>}
                <span className={styles.itemName}>{item.name}</span>
              </div>
              {item.buyerName && (
                <div className={styles.itemRow}>
                  <PersonOutline className={styles.itemIcon} />
                  <span className={styles.itemBuyerLabel}>
                    {t("components.homeCalendarDayModal.buyer")}:
                  </span>
                  <span className={styles.itemBuyerName}>{item.buyerName}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </dialog>
  );
};

export default HomeCalendarDayModal;
